import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck, faArrowLeft, faArrowRight, faEye, faEyeSlash,
  faUniversity, faLock, faShieldAlt, faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import gsap from 'gsap';
import { useAuth } from '../context/AuthContext';
import { authAPI, userAPI } from '../services/api';
import Toast from './Toast';
import Alert from './Alert';
// Light-colored logo variant — reads correctly against this page's dark background.
const autoboyLogo = '/autoboy_logo3.png';
import sellerAuthVisual from '../assets/auth/seller-auth-visual.png';

const TRUST_CHIPS = [
  { icon: 'fa-store', label: '2K+ Sellers' },
  { icon: 'fa-sack-dollar', label: '₦500M+ Transactions' },
  { icon: 'fa-truck-fast', label: '99.2% Delivery Rate' },
];

const STEP_LABELS = ['Country', 'Email', 'Bank', 'Business', 'Security'];

// ── Shared field primitives (Tailwind, dark theme) ──────────────────────
const FieldLabel = ({ children }) => (
  <label className="tw-block tw-text-sm tw-font-medium tw-text-white/80 tw-mb-2">{children}</label>
);

const FieldError = ({ children }) => (
  children ? <p className="tw-mt-1.5 tw-text-xs tw-font-medium tw-text-red-400">{children}</p> : null
);

const TextInput = ({ icon, className = '', ...props }) => (
  <div className="tw-relative">
    {icon && <i className={`fas ${icon} tw-absolute tw-left-4 tw-top-1/2 -tw-translate-y-1/2 tw-text-white/25 tw-text-sm`} />}
    <input
      {...props}
      className={`tw-w-full tw-rounded-xl tw-border tw-border-white/10 tw-bg-white/[0.04] ${icon ? 'tw-pl-11' : 'tw-pl-4'} tw-pr-4 tw-py-3.5 tw-text-white tw-placeholder-white/25 tw-outline-none tw-transition-all focus:tw-border-green-500 focus:tw-bg-white/[0.06] focus:tw-ring-4 focus:tw-ring-green-500/10 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed ${className}`}
    />
  </div>
);

const SelectInput = ({ children, className = '', ...props }) => (
  <div className="tw-relative">
    <select
      {...props}
      className={`tw-w-full tw-appearance-none tw-rounded-xl tw-border tw-border-white/10 tw-bg-white/[0.04] tw-pl-4 tw-pr-10 tw-py-3.5 tw-text-white tw-outline-none tw-transition-all focus:tw-border-green-500 focus:tw-bg-white/[0.06] focus:tw-ring-4 focus:tw-ring-green-500/10 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed [&>option]:tw-bg-[#111111] ${className}`}
    >
      {children}
    </select>
    <i className="fas fa-chevron-down tw-absolute tw-right-4 tw-top-1/2 -tw-translate-y-1/2 tw-text-white/30 tw-text-xs tw-pointer-events-none" />
  </div>
);

const SellerSignup = () => {
  const navigate = useNavigate();
  const { register } = useAuth();

  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [toast, setToast] = useState(null);
  const [alert, setAlert] = useState(null);

  const [formData, setFormData] = useState({
    country: '',
    email: '',
    // Bank verification fields (Step 3)
    bvn: '',
    bankName: '',
    bankCode: '',
    accountNumber: '',
    accountName: '', // Verified from Monnify
    // Personal details (Step 4)
    firstName: '',
    lastName: '',
    phone: '',
    // Business details (Step 4)
    accountType: '',
    shopName: '',
    shopLocation: '',
    businessAddress: '',
    // Password (Step 5)
    password: '',
    confirmPassword: '',
    agreeToTerms: false
  });

  const [isVerifyingBank, setIsVerifyingBank] = useState(false);
  const [bankVerified, setBankVerified] = useState(false);
  const [namesLocked, setNamesLocked] = useState(false);
  const [availableBanks, setAvailableBanks] = useState([]);

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const visualRef = useRef(null);
  const stepWrapperRef = useRef(null);

  // Fetch Nigerian banks list on mount — live Monnify list, hardcoded fallback
  useEffect(() => {
    const FALLBACK_BANKS = [
      { name: 'Access Bank', code: '044' },
      { name: 'Ecobank Nigeria', code: '050' },
      { name: 'Fidelity Bank', code: '070' },
      { name: 'First Bank of Nigeria', code: '011' },
      { name: 'First City Monument Bank', code: '214' },
      { name: 'Guaranty Trust Bank', code: '058' },
      { name: 'Keystone Bank', code: '082' },
      { name: 'Kuda Microfinance Bank', code: '50211' },
      { name: 'Moniepoint MFB', code: '50515' },
      { name: 'OPay', code: '999992' },
      { name: 'PalmPay', code: '999991' },
      { name: 'Polaris Bank', code: '076' },
      { name: 'Providus Bank', code: '101' },
      { name: 'Stanbic IBTC Bank', code: '221' },
      { name: 'Sterling Bank', code: '232' },
      { name: 'Union Bank of Nigeria', code: '032' },
      { name: 'United Bank for Africa', code: '033' },
      { name: 'Wema Bank', code: '035' },
      { name: 'Zenith Bank', code: '057' }
    ];
    const fetchBanks = async () => {
      try {
        const response = await userAPI.getBanks();
        const list = response?.data || response || [];
        if (Array.isArray(list) && list.length > 0) {
          setAvailableBanks(list.filter(b => b?.name && b?.code));
          return;
        }
        setAvailableBanks(FALLBACK_BANKS);
      } catch (error) {
        setAvailableBanks(FALLBACK_BANKS);
      }
    };
    fetchBanks();
  }, []);

  // Crossfade the step content whenever the step changes.
  useEffect(() => {
    gsap.fromTo(
      stepWrapperRef.current,
      { opacity: 0, y: 14 },
      { opacity: 1, y: 0, duration: 0.45, ease: 'power3.out' }
    );
  }, [currentStep]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 24;
      const y = (e.clientY / window.innerHeight - 0.5) * 24;
      gsap.to(visualRef.current, { x, y, duration: 1.2, ease: 'power2.out' });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Form handlers - memoized to prevent re-creation
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => {
      if (prev[field]) {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      }
      return prev;
    });

    // Auto-populate bank code when bank is selected
    if (field === 'bankName') {
      const selectedBank = availableBanks.find(bank => bank.name === value);
      if (selectedBank) {
        setFormData(prev => ({ ...prev, bankCode: selectedBank.code }));
      }
    }
  }, [availableBanks]);

  // Bank verification handler
  const handleVerifyBankAccount = async () => {
    const newErrors = {};
    if (!formData.bankName) newErrors.bankName = 'Please select your bank';
    if (!formData.accountNumber || formData.accountNumber.length !== 10) {
      newErrors.accountNumber = 'Account number must be 10 digits';
    }
    if (formData.bvn && formData.bvn.length !== 11) {
      newErrors.bvn = 'BVN must be 11 digits';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsVerifyingBank(true);
    try {
      const response = await authAPI.verifyBankAccount(formData.accountNumber, formData.bankCode);

      if (response && (response.success || response.data)) {
        const accountData = response.data || response;
        const verifiedName = accountData.account_name || accountData.accountName;

        if (!verifiedName) {
          throw new Error('Account name not returned from verification. Please try again.');
        }

        const nameParts = verifiedName.trim().split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || nameParts[0];

        setFormData(prev => ({
          ...prev,
          accountName: verifiedName,
          firstName: firstName,
          lastName: lastName
        }));

        setBankVerified(true);
        setNamesLocked(true);

        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.bankVerification;
          return newErrors;
        });

        setToast({
          message: `Account verified: ${verifiedName}`,
          type: 'success'
        });
      } else {
        throw new Error(response?.message || 'Unable to verify account. Please check your details.');
      }
    } catch (error) {
      setToast({
        message: error.message || 'Failed to verify bank account. Please check your details and try again.',
        type: 'error'
      });
      setBankVerified(false);
      setErrors(prev => ({ ...prev, bankVerification: error.message || 'Bank verification failed' }));
    } finally {
      setIsVerifyingBank(false);
    }
  };

  // Validation functions
  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData.country) newErrors.country = 'Please select a country';
        break;
      case 2:
        if (!formData.email) {
          newErrors.email = 'Please enter your email';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
          newErrors.email = 'Please enter a valid email';
        }
        break;
      case 3:
        if (!bankVerified) {
          newErrors.bankVerification = 'Please verify your bank account first';
        }
        if (!formData.bankName) newErrors.bankName = 'Please select your bank';
        if (!formData.accountNumber || formData.accountNumber.length !== 10) {
          newErrors.accountNumber = 'Account number must be 10 digits';
        }
        if (formData.bvn && formData.bvn.length !== 11) {
          newErrors.bvn = 'BVN must be 11 digits';
        }
        break;
      case 4:
        if (!formData.firstName) newErrors.firstName = 'First name is required';
        if (!formData.lastName) newErrors.lastName = 'Last name is required';
        if (!formData.accountType) newErrors.accountType = 'Please select account type';
        if (!formData.shopName) newErrors.shopName = 'Please enter shop/business name';
        if (!formData.shopLocation) newErrors.shopLocation = 'Please enter shop location';
        if (!formData.phone) newErrors.phone = 'Please enter phone number';
        break;
      case 5:
        if (!formData.password) {
          newErrors.password = 'Please enter password';
        } else if (formData.password.length < 8) {
          newErrors.password = 'Password must be at least 8 characters';
        } else {
          const hasUpper = /[A-Z]/.test(formData.password);
          const hasLower = /[a-z]/.test(formData.password);
          const hasNumber = /\d/.test(formData.password);
          const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password);

          if (!hasUpper || !hasLower || !hasNumber || !hasSpecial) {
            newErrors.password = 'Password must contain uppercase, lowercase, number and special character';
          }
        }
        if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = 'Passwords do not match';
        }
        if (!formData.agreeToTerms) newErrors.agreeToTerms = 'Please agree to terms and conditions';
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = async () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (validateStep(5)) {
      setIsLoading(true);

      try {
        const registrationData = {
          email: formData.email,
          password: formData.password,
          first_name: formData.firstName,
          last_name: formData.lastName,
          username: formData.email.split('@')[0].length < 3
            ? formData.email.replace(/[@.]/g, '').substring(0, 30)
            : formData.email.split('@')[0].substring(0, 30),
          phone: `+234${formData.phone}`,
          user_type: 'seller',
          accept_terms: formData.agreeToTerms,
          shop_name: formData.shopName,
          shop_location: formData.shopLocation,
          account_type: formData.accountType,
          bvn: formData.bvn,
          bank_name: formData.bankName,
          bank_code: formData.bankCode,
          account_number: formData.accountNumber,
          account_name: formData.accountName,
          business_address: formData.businessAddress || formData.shopLocation
        };

        await register(registrationData);

        setToast({
          message: 'Seller account created! Please check your email to verify your account before logging in.',
          type: 'success'
        });

        setTimeout(() => {
          navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`);
        }, 2000);

      } catch (error) {
        setToast({
          message: error.message || 'Registration failed. Please try again.',
          type: 'error'
        });
        setIsLoading(false);
      }
    }
  };

  // ── Step components ────────────────────────────────────────────────
  const Step1 = useMemo(() => (
    <div>
      <FieldLabel>Select your country</FieldLabel>
      <SelectInput value={formData.country} onChange={(e) => handleInputChange('country', e.target.value)}>
        <option value="">Select your country</option>
        <option value="NG">Nigeria</option>
        <option value="GH">Ghana</option>
        <option value="KE">Kenya</option>
        <option value="ZA">South Africa</option>
        <option value="EG">Egypt</option>
      </SelectInput>
      <FieldError>{errors.country}</FieldError>
    </div>
  ), [formData.country, errors.country, handleInputChange]);

  const Step2 = useMemo(() => (
    <div>
      <FieldLabel>Enter your email</FieldLabel>
      <TextInput
        icon="fa-envelope"
        type="email"
        placeholder="you@example.com"
        value={formData.email}
        onChange={(e) => handleInputChange('email', e.target.value)}
        autoComplete="email"
      />
      <FieldError>{errors.email}</FieldError>
    </div>
  ), [formData.email, errors.email, handleInputChange]);

  const Step3 = useMemo(() => (
    <div className="tw-flex tw-flex-col tw-gap-5">
      <div className="tw-text-center tw-mb-1">
        <div className="tw-inline-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-green-500/10 tw-border tw-border-green-500/20 tw-mb-3">
          <FontAwesomeIcon icon={faUniversity} className="tw-text-green-400 tw-text-lg" />
        </div>
        <h4 className="tw-text-white tw-font-semibold tw-mb-1.5">Bank Account Verification</h4>
        <p className="tw-text-white/45 tw-text-sm tw-leading-relaxed">
          We'll verify your bank account to ensure secure transactions. Your name will be auto-populated from your bank account.
        </p>
      </div>

      <div>
        <FieldLabel>Bank Name *</FieldLabel>
        <SelectInput
          value={formData.bankName}
          onChange={(e) => handleInputChange('bankName', e.target.value)}
          disabled={bankVerified}
        >
          <option value="">Select your bank</option>
          {availableBanks.map((bank) => (
            <option key={bank.code} value={bank.name}>{bank.name}</option>
          ))}
        </SelectInput>
        <FieldError>{errors.bankName}</FieldError>
      </div>

      <div>
        <FieldLabel>Account Number *</FieldLabel>
        <TextInput
          icon="fa-hashtag"
          type="tel"
          placeholder="Enter 10-digit account number"
          value={formData.accountNumber}
          onChange={(e) => handleInputChange('accountNumber', e.target.value.replace(/\D/g, '').slice(0, 10))}
          maxLength="10"
          disabled={bankVerified}
        />
        <FieldError>{errors.accountNumber}</FieldError>
      </div>

      <div>
        <FieldLabel>BVN (Bank Verification Number) <span className="tw-text-white/35 tw-font-normal">(Optional)</span></FieldLabel>
        <TextInput
          icon="fa-id-card"
          type="tel"
          placeholder="Enter 11-digit BVN"
          value={formData.bvn}
          onChange={(e) => handleInputChange('bvn', e.target.value.replace(/\D/g, '').slice(0, 11))}
          maxLength="11"
          disabled={bankVerified}
        />
        <FieldError>{errors.bvn}</FieldError>
      </div>

      {bankVerified && formData.accountName && (
        <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-green-500/25 tw-bg-green-500/10 tw-px-4 tw-py-3.5">
          <FontAwesomeIcon icon={faShieldAlt} className="tw-text-green-400 tw-text-lg tw-shrink-0" />
          <div>
            <p className="tw-text-sm tw-font-bold tw-text-green-400">Account Verified</p>
            <p className="tw-text-sm tw-text-white/70">{formData.accountName}</p>
          </div>
        </div>
      )}

      {!bankVerified && (
        <button
          type="button"
          onClick={handleVerifyBankAccount}
          disabled={isVerifyingBank}
          className="tw-w-full tw-rounded-xl tw-border tw-border-green-500/40 tw-bg-green-500/10 tw-py-3.5 tw-text-sm tw-font-bold tw-text-green-400 tw-transition-all hover:tw-bg-green-500/15 hover:tw-border-green-500/60 disabled:tw-opacity-60 disabled:tw-pointer-events-none"
        >
          {isVerifyingBank ? (
            <><FontAwesomeIcon icon={faShieldAlt} spin className="tw-mr-2" />Verifying...</>
          ) : (
            <><FontAwesomeIcon icon={faShieldAlt} className="tw-mr-2" />Verify Bank Account</>
          )}
        </button>
      )}

      <FieldError>{errors.bankVerification}</FieldError>
    </div>
  ), [formData.bankName, formData.accountNumber, formData.bvn, formData.accountName, bankVerified, isVerifyingBank, errors, availableBanks, handleInputChange]);

  const Step4 = useMemo(() => (
    <div className="tw-flex tw-flex-col tw-gap-5">
      {namesLocked && (
        <div className="tw-flex tw-items-start tw-gap-2.5 tw-rounded-xl tw-border tw-border-amber-500/25 tw-bg-amber-500/10 tw-px-4 tw-py-3 tw-text-sm tw-text-amber-300">
          <FontAwesomeIcon icon={faExclamationTriangle} className="tw-mt-0.5 tw-shrink-0" />
          <p>Your first and last names are locked based on your verified bank account. Only change if incorrect.</p>
        </div>
      )}

      <div className="tw-grid tw-grid-cols-2 tw-gap-4">
        <div>
          <FieldLabel>
            First Name * {namesLocked && <FontAwesomeIcon icon={faLock} className="tw-text-white/30 tw-ml-1 tw-text-xs" />}
          </FieldLabel>
          <TextInput
            type="text"
            placeholder="First name"
            value={formData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            readOnly={namesLocked}
            disabled={namesLocked}
          />
          <FieldError>{errors.firstName}</FieldError>
        </div>

        <div>
          <FieldLabel>
            Last Name * {namesLocked && <FontAwesomeIcon icon={faLock} className="tw-text-white/30 tw-ml-1 tw-text-xs" />}
          </FieldLabel>
          <TextInput
            type="text"
            placeholder="Last name"
            value={formData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            readOnly={namesLocked}
            disabled={namesLocked}
          />
          <FieldError>{errors.lastName}</FieldError>
        </div>
      </div>

      <div>
        <FieldLabel>Account Type *</FieldLabel>
        <div className="tw-grid tw-grid-cols-2 tw-gap-3">
          {[{ value: 'business', label: 'Business' }, { value: 'individual', label: 'Individual' }].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => handleInputChange('accountType', opt.value)}
              className={`tw-rounded-xl tw-border tw-py-3 tw-text-sm tw-font-semibold tw-transition-all ${
                formData.accountType === opt.value
                  ? 'tw-border-green-500 tw-bg-green-500/15 tw-text-green-400'
                  : 'tw-border-white/10 tw-bg-white/[0.03] tw-text-white/60 hover:tw-border-white/20'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <FieldError>{errors.accountType}</FieldError>
      </div>

      <div>
        <FieldLabel>Shop/Business Name *</FieldLabel>
        <TextInput
          icon="fa-shop"
          type="text"
          placeholder="Enter your shop or business name"
          value={formData.shopName}
          onChange={(e) => handleInputChange('shopName', e.target.value)}
        />
        <p className="tw-mt-1.5 tw-text-xs tw-text-white/35">Your business name can be different from your personal name</p>
        <FieldError>{errors.shopName}</FieldError>
      </div>

      <div>
        <FieldLabel>Shop Location *</FieldLabel>
        <TextInput
          icon="fa-location-dot"
          type="text"
          placeholder="City, State (e.g., Lagos, Lagos)"
          value={formData.shopLocation}
          onChange={(e) => handleInputChange('shopLocation', e.target.value)}
        />
        <FieldError>{errors.shopLocation}</FieldError>
      </div>

      <div>
        <FieldLabel>Business Address</FieldLabel>
        <TextInput
          icon="fa-map"
          type="text"
          placeholder="Full business address (optional)"
          value={formData.businessAddress}
          onChange={(e) => handleInputChange('businessAddress', e.target.value)}
        />
      </div>

      <div>
        <FieldLabel>Phone Number *</FieldLabel>
        <div className="tw-flex tw-rounded-xl tw-border tw-border-white/10 tw-bg-white/[0.04] tw-overflow-hidden focus-within:tw-border-green-500 focus-within:tw-ring-4 focus-within:tw-ring-green-500/10 tw-transition-all">
          <span className="tw-flex tw-items-center tw-px-4 tw-text-white/50 tw-text-sm tw-font-medium tw-border-r tw-border-white/10">+234</span>
          <input
            type="tel"
            placeholder="Phone number"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
            maxLength="10"
            className="tw-flex-1 tw-min-w-0 tw-bg-transparent tw-px-4 tw-py-3.5 tw-text-white tw-placeholder-white/25 tw-outline-none"
          />
        </div>
        <FieldError>{errors.phone}</FieldError>
      </div>
    </div>
  ), [formData, errors, namesLocked, handleInputChange]);

  const Step5 = useMemo(() => (
    <div className="tw-flex tw-flex-col tw-gap-5">
      <div>
        <FieldLabel>Password *</FieldLabel>
        <div className="tw-relative">
          <i className="fas fa-lock tw-absolute tw-left-4 tw-top-1/2 -tw-translate-y-1/2 tw-text-white/25 tw-text-sm" />
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="Create password"
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            className="tw-w-full tw-rounded-xl tw-border tw-border-white/10 tw-bg-white/[0.04] tw-pl-11 tw-pr-11 tw-py-3.5 tw-text-white tw-placeholder-white/25 tw-outline-none tw-transition-all focus:tw-border-green-500 focus:tw-bg-white/[0.06] focus:tw-ring-4 focus:tw-ring-green-500/10"
          />
          <button
            type="button"
            className="tw-absolute tw-right-4 tw-top-1/2 -tw-translate-y-1/2 tw-text-white/35 hover:tw-text-green-400 tw-transition-colors"
            onClick={() => setShowPassword(!showPassword)}
          >
            <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
          </button>
        </div>
        <FieldError>{errors.password}</FieldError>
      </div>

      <div>
        <FieldLabel>Confirm Password *</FieldLabel>
        <div className="tw-relative">
          <i className="fas fa-lock tw-absolute tw-left-4 tw-top-1/2 -tw-translate-y-1/2 tw-text-white/25 tw-text-sm" />
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="Confirm password"
            value={formData.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
            className="tw-w-full tw-rounded-xl tw-border tw-border-white/10 tw-bg-white/[0.04] tw-pl-11 tw-pr-11 tw-py-3.5 tw-text-white tw-placeholder-white/25 tw-outline-none tw-transition-all focus:tw-border-green-500 focus:tw-bg-white/[0.06] focus:tw-ring-4 focus:tw-ring-green-500/10"
          />
          <button
            type="button"
            className="tw-absolute tw-right-4 tw-top-1/2 -tw-translate-y-1/2 tw-text-white/35 hover:tw-text-green-400 tw-transition-colors"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          >
            <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
          </button>
        </div>
        <FieldError>{errors.confirmPassword}</FieldError>
      </div>

      <div className="tw-rounded-xl tw-border tw-border-white/10 tw-bg-white/[0.03] tw-px-4 tw-py-3.5">
        <p className="tw-text-xs tw-font-semibold tw-text-white/60 tw-mb-2">Password must contain:</p>
        <ul className="tw-grid tw-grid-cols-2 tw-gap-x-3 tw-gap-y-1.5 tw-text-xs tw-text-white/40">
          <li>At least 8 characters</li>
          <li>Capital letter (A-Z)</li>
          <li>Lowercase letter (a-z)</li>
          <li>Number (0-9)</li>
          <li>Special character (!@#$%^&*)</li>
        </ul>
      </div>

      <div>
        <label className="tw-flex tw-items-start tw-gap-2.5 tw-cursor-pointer">
          <input
            type="checkbox"
            checked={formData.agreeToTerms}
            onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
            className="tw-mt-0.5 tw-h-4 tw-w-4 tw-rounded tw-accent-green-500 tw-cursor-pointer tw-shrink-0"
          />
          <span className="tw-text-sm tw-text-white/60 tw-leading-relaxed">
            I agree to the{' '}
            <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="tw-text-green-400 hover:tw-text-green-300 tw-font-semibold tw-underline tw-underline-offset-2">Terms and Conditions</a>
            {' '}and{' '}
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="tw-text-green-400 hover:tw-text-green-300 tw-font-semibold tw-underline tw-underline-offset-2">Privacy Policy</a>
            {' '}of AutoboyExpress marketplace
          </span>
        </label>
        <FieldError>{errors.agreeToTerms}</FieldError>
      </div>
    </div>
  ), [formData.password, formData.confirmPassword, formData.agreeToTerms, errors, showPassword, showConfirmPassword, handleInputChange]);

  const renderStep = () => {
    switch (currentStep) {
      case 1: return Step1;
      case 2: return Step2;
      case 3: return Step3;
      case 4: return Step4;
      case 5: return Step5;
      default: return Step1;
    }
  };

  return (
    <div className="tw-min-h-dvh tw-w-full tw-bg-[#0A0A0A] tw-flex tw-flex-col lg:tw-flex-row tw-font-['Epilogue',_sans-serif]">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      {alert && <Alert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />}

      {/* Left — Brand Visual */}
      <div className="tw-relative tw-hidden lg:tw-flex lg:tw-w-[46%] xl:tw-w-1/2 tw-flex-col tw-justify-between tw-overflow-hidden tw-bg-black">
        <img
          ref={visualRef}
          src={sellerAuthVisual}
          alt=""
          aria-hidden="true"
          className="tw-absolute tw-inset-0 tw-h-[110%] tw-w-[110%] -tw-top-[5%] -tw-left-[5%] tw-object-cover tw-scale-105"
        />
        <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-black tw-via-black/60 tw-to-black/20" />
        <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-r tw-from-black/40 tw-via-transparent tw-to-transparent" />

        <div className="tw-relative tw-z-10 tw-p-10 xl:tw-p-14">
          <a href="/homepage" className="tw-inline-flex tw-items-center tw-gap-2 tw-text-white/90 hover:tw-text-white tw-transition-colors tw-text-sm tw-font-medium">
            <i className="fas fa-arrow-left tw-text-xs" />
            Back to shop
          </a>
        </div>

        <div className="tw-relative tw-z-10 tw-p-10 xl:tw-p-14 tw-pb-14 xl:tw-pb-16">
          <img src={autoboyLogo} alt="AutoboyExpress" className="tw-h-36 xl:tw-h-44 tw-w-auto tw-mb-8 tw-drop-shadow-[0_2px_28px_rgba(34,197,94,0.45)]" />
          <h1 className="tw-text-[2.5rem] xl:tw-text-[3rem] tw-font-extrabold tw-leading-[1.05] tw-tracking-tight tw-text-white tw-mb-4">
            Turn your inventory<br />into
            <span className="tw-bg-gradient-to-r tw-from-green-400 tw-to-green-500 tw-bg-clip-text tw-text-transparent"> income.</span>
          </h1>
          <p className="tw-text-white/60 tw-text-base tw-max-w-md tw-mb-9 tw-leading-relaxed">
            List your products, reach thousands of buyers, and get paid securely into your bank account.
          </p>
          <div className="tw-flex tw-flex-wrap tw-gap-3">
            {TRUST_CHIPS.map((chip) => (
              <div key={chip.label} className="tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-white/10 tw-bg-white/[0.06] tw-backdrop-blur-xl tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-text-white/85">
                <i className={`fas ${chip.icon} tw-text-green-400`} />
                {chip.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right — Multi-step Form */}
      <div className="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-bg-[#0A0A0A] tw-px-6 tw-py-10 sm:tw-px-10 lg:tw-px-16 tw-min-h-dvh tw-overflow-y-auto">
        <div className="tw-flex lg:tw-hidden tw-w-full tw-max-w-[440px] tw-items-center tw-justify-between tw-mb-8">
          <a href="/homepage" className="tw-inline-flex tw-items-center tw-gap-2 tw-text-white/60 hover:tw-text-white tw-transition-colors tw-text-sm tw-font-medium">
            <i className="fas fa-arrow-left tw-text-xs" />
            Back
          </a>
          <img src={autoboyLogo} alt="AutoboyExpress" className="tw-h-20 tw-w-auto" />
        </div>

        <div className="tw-w-full tw-max-w-[440px] tw-py-6">
          <img src={autoboyLogo} alt="AutoboyExpress" className="tw-hidden lg:tw-block tw-h-24 tw-w-auto tw-mb-5 tw-mx-auto" />

          {/* Progress */}
          <div className="tw-mb-7">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
              <span className="tw-text-xs tw-font-bold tw-text-green-400 tw-uppercase tw-tracking-wider">{STEP_LABELS[currentStep - 1]}</span>
              <span className="tw-text-xs tw-font-medium tw-text-white/35">Step {currentStep} of 5</span>
            </div>
            <div className="tw-h-1.5 tw-w-full tw-rounded-full tw-bg-white/[0.06] tw-overflow-hidden">
              <div
                className="tw-h-full tw-rounded-full tw-bg-gradient-to-r tw-from-green-500 tw-to-green-400 tw-transition-all tw-duration-500 tw-ease-out"
                style={{ width: `${(currentStep / 5) * 100}%` }}
              />
            </div>
          </div>

          <h2 className="tw-text-2xl sm:tw-text-3xl tw-font-bold tw-text-white tw-mb-6 tw-tracking-tight">
            Sell on AutoboyExpress
          </h2>

          <div ref={stepWrapperRef}>
            {renderStep()}
          </div>

          <div className="tw-flex tw-gap-3 tw-mt-8">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={prevStep}
                disabled={isLoading || isVerifyingBank}
                className="tw-flex-1 tw-rounded-xl tw-border tw-border-white/10 tw-bg-white/[0.03] tw-py-3.5 tw-text-sm tw-font-bold tw-text-white/70 tw-transition-all hover:tw-bg-white/[0.06] hover:tw-text-white disabled:tw-opacity-50 disabled:tw-pointer-events-none"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="tw-mr-2" />
                Back
              </button>
            )}

            {currentStep < 5 ? (
              <button
                type="button"
                onClick={nextStep}
                disabled={isLoading || isVerifyingBank}
                className="tw-flex-[2] tw-rounded-xl tw-bg-gradient-to-r tw-from-green-500 tw-to-green-600 tw-py-3.5 tw-text-sm tw-font-bold tw-text-white tw-shadow-lg tw-shadow-green-500/20 tw-transition-all hover:-tw-translate-y-0.5 hover:tw-shadow-xl hover:tw-shadow-green-500/30 active:tw-translate-y-0 active:tw-scale-[0.98] disabled:tw-opacity-60 disabled:tw-pointer-events-none"
              >
                {isLoading ? 'Loading...' : <>Next <FontAwesomeIcon icon={faArrowRight} className="tw-ml-2" /></>}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isLoading}
                className="tw-flex-[2] tw-rounded-xl tw-bg-gradient-to-r tw-from-green-500 tw-to-green-600 tw-py-3.5 tw-text-sm tw-font-bold tw-text-white tw-shadow-lg tw-shadow-green-500/20 tw-transition-all hover:-tw-translate-y-0.5 hover:tw-shadow-xl hover:tw-shadow-green-500/30 active:tw-translate-y-0 active:tw-scale-[0.98] disabled:tw-opacity-60 disabled:tw-pointer-events-none"
              >
                {isLoading ? 'Creating Account...' : <>Complete Registration <FontAwesomeIcon icon={faCheck} className="tw-ml-2" /></>}
              </button>
            )}
          </div>

          <p className="tw-text-center tw-text-sm tw-text-white/45 tw-mt-7">
            Already have an account?{' '}
            <a href="/seller-signin" className="tw-font-semibold tw-text-green-400 hover:tw-text-green-300 tw-transition-colors">Sign In</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SellerSignup;
